import { Autocomplete, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { DeleteOutline } from "@mui/icons-material";
import { UOM_LIST } from "../helper";
import { toast } from "react-toastify";

const AddItemModal = ({
  poItem,
  lineItem,
  updateItem,
  onDelete,
  vendorCode,
  isItemsUploaded,
  isItemDeleted,
  isRateContractEnforced = false,
  prQuantity,
  isPRLinked = false,
  subcategoryList = [],
  poDate,
  rateContractMissing = false,
}) => {
  const [formDetails, setFormDetails] = useState({});
  const [items, setItems] = useState([]);
  const [itemMappingError, setItemMappingError] = useState("");
  const [prRequiredError, setPrRequiredError] = useState("");

  useEffect(() => {
    if (isItemsUploaded) {
      setItemMappingError("");
    }
  }, [isItemsUploaded]);

  useEffect(() => {
    const hasPreFilledData = !!(poItem?.item?.id) && !formDetails.item;
    if ((isItemsUploaded && !formDetails.item) || isItemDeleted || hasPreFilledData) {
      const newFormDetails = {
        ...poItem,
      };

      if (!newFormDetails.uom) {
        newFormDetails.uom = UOM_LIST[0];
      } else if (typeof newFormDetails.uom === "string") {
        newFormDetails.uom =
          UOM_LIST.find((item) => item.value === newFormDetails.uom) ||
          UOM_LIST[0];
      }
      if (isItemsUploaded || hasPreFilledData) {
        setItems([{ ...poItem.item }]);
      }
      setFormDetails(newFormDetails);
    }
  }, [poItem, isItemDeleted]);

  const loadItems = (subcategory) => {
    const { id = "" } = subcategory || {};
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_ITEMS_BY_VENDOR}`,
      null,
      {
        page: 1,
        subCategory: id,
        vendorCode,
        limit: 1000,
        excludeRateContractRequired: true,
        ...(poDate ? { poDate } : {}),
      }
    )
      .then((response) => {
        if (response.message) {
        } else {
          setItems(response || []);
        }
      })
      .catch((err) => {});
  };

  const getTaxAmount = () => {
    const { rate = 0, quantity = 0, taxPercentage = 0 } = formDetails;
    return (rate * quantity * (taxPercentage / 100)).toFixed(2);
  };

  const getAmount = () => {
    const { rate = 0, quantity = 0 } = formDetails;
    return (rate * quantity).toFixed(2);
  };

  const getTotalAmount = () => {
    const { rate = 0, quantity = 0, taxPercentage = 0 } = formDetails;
    let taxAmount = rate * quantity * (taxPercentage / 100);
    return (rate * quantity + +taxAmount).toFixed(2);
  };

  const onItemChange = (item) => {
    if (item?.prRequired) {
      setPrRequiredError(
        "PR is required for this item. Please create a PR before creating a PO."
      );
      toast.error(
        "PR is required for this item. Please create a PR before creating a PO."
      );
      return;
    }
    if (item) {
      setPrRequiredError("");
      setItemMappingError("");
      const isRateContractItem = !!item.rateContractRequired;
      const baseDetails = {
        ...formDetails,
        item: item,
        rate: isRateContractItem ? item.contractRate : item.price,
        // subCategory is intentionally NOT overwritten with item.subCategory here: that's
        // just the item's deprecated "primary" mirror, which can differ from the specific
        // subcategory the user picked in the dropdown above (an item can now have more
        // than one categoryMappings pair). formDetails.subCategory (already set by the
        // Subcategory Autocomplete's own onChange) is the correct, unambiguous selector.
        refRate: item.price,
        hsnCode: item.hsnCode || "",
        quantity: 1,
        taxPercentage: isRateContractItem ? item.contractTaxPercentage : item.taxPercentage,
        uom: UOM_LIST.find((uom) => item.uom == uom.value) || UOM_LIST[0],
        itemId: item.id,
        lineItemId: lineItem.id,
        vendorMappingStatus: isItemsUploaded ? undefined : null,
        hasApprovedVendorMapping: isRateContractItem ? true : !!isItemsUploaded,
      };
      setFormDetails(baseDetails);
      onFormChange(baseDetails);

      // Rate-contract-required items already had their contract resolved by the
      // item list fetch (only contract-covered items are selectable) -- no need
      // for the extra dept-level mapping lookup below for those.
      if (
        isRateContractEnforced &&
        !isRateContractItem &&
        !isItemsUploaded &&
        vendorCode &&
        item?.id
      ) {
        invokeApi(
          HTTP_METHODS.GET,
          `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.VENDOR_MAPPING_GET_MAPPING}`,
          null,
          { vendorCode, item: item.id, ...(poDate ? { date: poDate } : {}) }
        )
          .then((response) => {
            const isEmptyResponse =
              !response ||
              (Array.isArray(response) && response.length === 0) ||
              (!Array.isArray(response) &&
                typeof response === "object" &&
                Object.keys(response).length === 0);
            if (isEmptyResponse || response?.message || response?.code) {
              const itemName = item?.name || "selected item";
              setItemMappingError(`Vendor mapping not present for ${itemName}.`);
              toast.warning(`Vendor mapping not present for ${itemName}.`, {
                autoClose: 2500,
              });
              setFormDetails((prev) => {
                const next = {
                  ...prev,
                  vendorMappingStatus: null,
                  hasApprovedVendorMapping: false,
                };
                onFormChange(next);
                return next;
              });
              return;
            }
            // A Vendor Item Mapping must never blindly override the Item Master price --
            // compare the two and use whichever is higher, along with its own paired tax
            // rate (never mix a price from one source with a tax rate from the other).
            const vendorPrice = response?.price != null ? parseFloat(response.price) : NaN;
            const vendorTax = response?.taxPercentage != null ? parseFloat(response.taxPercentage) : 0;
            const masterPrice = item.price != null ? parseFloat(item.price) : NaN;
            const masterTax = item.taxPercentage != null ? parseFloat(item.taxPercentage) : 0;
            let mappedRate;
            let mappedTax;
            if (Number.isFinite(vendorPrice) && Number.isFinite(masterPrice)) {
              const vendorWins = vendorPrice >= masterPrice;
              mappedRate = vendorWins ? vendorPrice : masterPrice;
              mappedTax = vendorWins ? vendorTax : masterTax;
            } else if (Number.isFinite(vendorPrice)) {
              mappedRate = vendorPrice;
              mappedTax = vendorTax;
            } else {
              mappedRate = Number.isFinite(masterPrice) ? masterPrice : 0;
              mappedTax = masterTax;
            }
            const approved = response?.status === "approved";
            if (!approved) {
              const itemName = item?.name || "selected item";
              setItemMappingError(
                `Vendor mapping not present for ${itemName}.`
              );
              toast.warning(`Vendor mapping not present for ${itemName}.`, {
                autoClose: 2500,
              });
            } else {
              setItemMappingError("");
            }

            setFormDetails((prev) => {
              const next = {
                ...prev,
                rate: mappedRate,
                taxPercentage: mappedTax,
                vendorMappingStatus: response?.status,
                hasApprovedVendorMapping: approved,
              };
              onFormChange(next);
              return next;
            });
          })
          .catch(() => {
            const itemName = item?.name || "selected item";
            setItemMappingError(`Vendor mapping not present for ${itemName}.`);
            toast.warning(`Vendor mapping not present for ${itemName}.`, {
              autoClose: 2500,
            });
          });
      }
    } else {
      setItemMappingError("");
      setPrRequiredError("");
      setFormDetails({
        ...formDetails,
        item: {},
        subCategory: {},
        rate: 0,
        refRate: 0,
        hsnCode: "",
        quantity: 0,
        taxPercentage: 0,
        vendorMappingStatus: null,
        hasApprovedVendorMapping: false,
      });
      onFormChange({
        ...formDetails,
        item: {},
        subCategory: {},
        rate: 0,
        refRate: 0,
        quantity: 0,
        hsnCode: "",
        taxPercentage: 0,
        vendorMappingStatus: null,
        hasApprovedVendorMapping: false,
      });
    }
  };

  const onInputChange = (event) => {
    setFormDetails({
      ...formDetails,
      [event.target.name]: event.target.value,
    });

    onFormChange({
      ...formDetails,
      [event.target.name]: event.target.value,
    });
  };

  const onFormChange = (newFormDetails) => {
    const {
      category = {},
      subCategory = {},
      item = {},
      uom = {},
    } = newFormDetails;

    updateItem({
      id: `${lineItem.id}-${category.id}-${item.id}`,
      mainCategoryName: category.name,
      mainCategoryId: category.id,
      // Category-mapping selector half -- see subCategory below. Without this the backend
      // never receives `category`, so it can't validate the {category, subCategory} pair
      // against the item's categoryMappings and falls back to (and fails on) the
      // single-mapping default for any multi-mapped item.
      category,
      subcategoryName: subCategory.name,
      subcategoryId: subCategory.id,
      subCategory,
      name: item.name,
      uom: uom.value,
      quantity: newFormDetails.quantity,
      rate: newFormDetails.rate,
      item,
      taxRate: newFormDetails.taxPercentage,
      taxPercentage: newFormDetails.taxPercentage,
      hsnCode: newFormDetails.hsnCode || "",
      specification: item.specification,
      itemId: item.id,
      lineItemId: lineItem.id,
      refRate: item.price,
      lineItemName: lineItem.name,
      availableAmount: lineItem.availableAmount,
      description: newFormDetails.description,
      remarks: newFormDetails.remarks,
      ticketNumber: newFormDetails.ticketNumber || "",
      ticketCreationDate: newFormDetails.ticketCreationDate || "",
      vendorMappingStatus: newFormDetails.vendorMappingStatus || null,
      hasApprovedVendorMapping: !!newFormDetails.hasApprovedVendorMapping,
    });
  };

  if (!isPRLinked) {
    return (
      <div
        className={`po-add-items-row ${
          lineItem?.category?.isTicketRequired && "ticket-required"
        }`}
      >
        <Autocomplete
          value={formDetails.subCategory || {}}
          onChange={(e, val) => {
            // Subcategory always resolves to exactly one parent category (enforced by the
            // Category/Subcategory master data), so its own populated `category` is always
            // an unambiguous category-mapping selector -- same principle the PR flow's
            // buildPayload already relies on. Falls back to whatever was already set so
            // clearing the dropdown (val == null) doesn't wipe a previously-resolved value.
            const resolvedCategory = val?.category || formDetails.category;
            if (val) {
              // category must also be persisted into local formDetails here, not just sent
              // up via onFormChange below -- onItemChange (fired next, when the user picks an
              // item) rebuilds its payload by spreading {...formDetails}, so if category were
              // only ever forwarded to the parent and never stored locally, that next
              // onFormChange call would see category as undefined and reset it to {} (its
              // destructuring default), silently wiping out the value just resolved here.
              setFormDetails({ ...formDetails, subCategory: val, category: resolvedCategory });
              loadItems(val);
            } else {
              setFormDetails((prevDetails) => ({ subCategory: null }));
            }
            onFormChange({
              category: resolvedCategory,
              subCategory: val || {},
            });
          }}
          getOptionLabel={(op) => op.name || ""}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Subcategory" />
          )}
          options={subcategoryList}
        />

        <Autocomplete
          value={formDetails.item || {}}
          onChange={(e, val) => {
            onItemChange(val);
          }}
          getOptionLabel={(op) => `${op.itemCode || ""} - ${op.name || ""}`}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Select Item"
              error={!!prRequiredError || (!isItemsUploaded && !!itemMappingError)}
              helperText={prRequiredError || ""}
            />
          )}
          options={items}
        />
        <Autocomplete
          value={formDetails.uom || ""}
          onChange={(e, val) => {
            setFormDetails({ ...formDetails, uom: val || "" });
            onFormChange({ ...formDetails, uom: val || "" });
          }}
          renderInput={(params) => (
            <TextField {...params} size="small" label="UOM" />
          )}
          options={UOM_LIST}
        />

        <TextField
          size="small"
          label="HSN Code"
          name="hsnCode"
          value={formDetails.hsnCode || ""}
          onChange={onInputChange}
        />

        <TextField
          size="small"
          label="Quantity"
          type="number"
          name="quantity"
          value={formDetails.quantity || ""}
          onChange={onInputChange}
        />
        <TextField
          size="small"
          label="Rate"
          type="number"
          name="rate"
          value={formDetails.rate || ""}
          onChange={onInputChange}
          disabled={isRateContractEnforced || !!formDetails.item?.rateContractRequired}
        />
        <TextField
          size="small"
          label="Amount"
          type="number"
          value={getAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Tax Rate"
          type="number"
          name="taxPercentage"
          value={
            formDetails?.hasOwnProperty("taxPercentage")
              ? formDetails.taxPercentage
              : ""
          }
          onChange={onInputChange}
          disabled={isRateContractEnforced || !!formDetails.item?.rateContractRequired}
        />

        <TextField
          size="small"
          label="Tax Amount"
          type="number"
          value={getTaxAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Ref Rate"
          type="number"
          value={(formDetails.item && formDetails.item.price) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Total Amount"
          type="number"
          value={getTotalAmount(formDetails) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Description"
          name="description"
          value={formDetails.description || ""}
          onChange={onInputChange}
        />
        <TextField
          size="small"
          label="Remarks"
          name="remarks"
          value={formDetails.remarks || ""}
          onChange={onInputChange}
        />
        {lineItem?.category?.isTicketRequired && (
          <>
            <TextField
              size="small"
              label="Ticket No."
              name="ticketNumber"
              value={formDetails.ticketNumber || ""}
              onChange={onInputChange}
              required
            />
            <TextField
              size="small"
              label="Ticket Creation Date"
              type="date"
              name="ticketCreationDate"
              value={
                formDetails.ticketCreationDate
                  ? new Date(formDetails.ticketCreationDate)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={onInputChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </>
        )}
        <IconButton
          disabled={lineItem.availableAmount <= 0}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(lineItem);
          }}
        >
          <DeleteOutline color="primary" />
        </IconButton>
      </div>
    );
  }

  return (
    <div className={`po-item-card${rateContractMissing ? " rate-contract-missing" : ""}`}>
      <div className="po-item-header">
        <span className="po-item-title">
          {formDetails.item?.name || formDetails.item?.itemCode || "New Item"}
        </span>
        {rateContractMissing && (
          <span className="rate-contract-missing-indicator" style={{ color: "#c62828", fontSize: "0.8rem" }}>
            No active rate contract for selected vendor
          </span>
        )}
        <IconButton
          className="delete-button"
          disabled={lineItem.availableAmount <= 0}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(lineItem);
          }}
        >
          <DeleteOutline color="primary" />
        </IconButton>
      </div>

      <div className="po-item-row item-info-row">
        {isPRLinked ? (
          <TextField
            size="small"
            label="Subcategory"
            value={
              formDetails.subCategory?.name ||
              formDetails.subcategoryName ||
              subcategoryList.find(
                (sc) =>
                  String(sc._id || sc.id) ===
                  String(
                    formDetails.subCategory?.id ??
                      formDetails.subcategoryId ??
                      ""
                  )
              )?.name ||
              ""
            }
            disabled
          />
        ) : (
          <Autocomplete
            value={formDetails.subCategory || {}}
            onChange={(e, val) => {
              // Subcategory always resolves to exactly one parent category (enforced by
              // the Category/Subcategory master data), so its own populated `category` is
              // always an unambiguous category-mapping selector -- same principle the PR
              // flow's buildPayload already relies on. Falls back to whatever was already
              // set so clearing the dropdown (val == null) doesn't wipe a previously-
              // resolved value.
              const resolvedCategory = val?.category || formDetails.category;
              if (val) {
                // category must also be persisted into local formDetails here, not just sent
                // up via onFormChange below -- see the other Subcategory Autocomplete above
                // for why (onItemChange rebuilds its payload from local formDetails next).
                setFormDetails({ ...formDetails, subCategory: val, category: resolvedCategory });
                loadItems(val);
              } else {
                setFormDetails((prevDetails) => ({ subCategory: null }));
              }
              onFormChange({
                category: resolvedCategory,
                subCategory: val || {},
              });
            }}
            getOptionLabel={(op) => op.name || ""}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Subcategory" />
            )}
            options={subcategoryList}
          />
        )}

        {isPRLinked ? (
          <TextField
            size="small"
            label="Select Item"
            value={
              formDetails.item?.itemCode
                ? `${formDetails.item.itemCode} - ${formDetails.item.name}`
                : formDetails.item?.name || formDetails.name || ""
            }
            disabled
          />
        ) : (
          <Autocomplete
            value={formDetails.item || {}}
            onChange={(e, val) => {
              onItemChange(val);
            }}
            getOptionLabel={(op) => `${op.itemCode || ""} - ${op.name || ""}`}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Select Item"
                error={!isItemsUploaded && !!itemMappingError}
              />
            )}
            options={items}
          />
        )}
        {isPRLinked ? (
          <TextField
            size="small"
            label="UOM"
            value={formDetails.uom?.label || formDetails.uom || ""}
            disabled
          />
        ) : (
          <Autocomplete
            value={formDetails.uom || ""}
            onChange={(e, val) => {
              setFormDetails({ ...formDetails, uom: val || "" });
              onFormChange({ ...formDetails, uom: val || "" });
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="UOM" />
            )}
            options={UOM_LIST}
          />
        )}

        <TextField
          size="small"
          label="HSN Code"
          name="hsnCode"
          value={formDetails.hsnCode || ""}
          onChange={onInputChange}
          disabled={isPRLinked}
        />
      </div>

      <div
        className={`po-item-row pricing-row${
          lineItem?.category?.isTicketRequired ? " has-ticket-fields" : ""
        }`}
      >
        <TextField
          size="small"
          label="Balance PR Quantity"
          type="number"
          value={prQuantity !== undefined ? prQuantity : ""}
          disabled
        />

        <TextField
          size="small"
          label="PO Quantity"
          type="number"
          name="quantity"
          value={formDetails.quantity || ""}
          onChange={(e) => {
            const raw = e.target.value;
            const clamped =
              prQuantity !== undefined && Number(raw) > prQuantity
                ? String(prQuantity)
                : raw;
            onInputChange({ target: { name: "quantity", value: clamped } });
          }}
          inputProps={{
            min: 0,
            ...(prQuantity !== undefined ? { max: prQuantity } : {}),
          }}
          disabled={isPRLinked && Number(prQuantity) === 0}
        />
        <TextField
          size="small"
          label="Rate"
          type="number"
          name="rate"
          value={formDetails.rate || ""}
          onChange={onInputChange}
          disabled={isRateContractEnforced || (isPRLinked && !rateContractMissing)}
        />
        <TextField
          size="small"
          label="Amount"
          type="number"
          value={getAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Tax Rate"
          type="number"
          name="taxPercentage"
          value={
            formDetails?.hasOwnProperty("taxPercentage")
              ? formDetails.taxPercentage
              : ""
          }
          onChange={onInputChange}
          disabled={isRateContractEnforced || (isPRLinked && !rateContractMissing)}
        />

        <TextField
          size="small"
          label="Tax Amount"
          type="number"
          value={getTaxAmount() || ""}
          disabled
        />
        <TextField
          size="small"
          label="Ref Rate"
          type="number"
          value={(formDetails.item && formDetails.item.price) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Total Amount"
          type="number"
          value={getTotalAmount(formDetails) || 0}
          disabled
        />
        <TextField
          size="small"
          label="Description"
          name="description"
          value={formDetails.description || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />
        <TextField
          size="small"
          label="Remarks"
          name="remarks"
          value={formDetails.remarks || ""}
          onChange={onInputChange}
          disabled={rateContractMissing}
        />
        {lineItem?.category?.isTicketRequired && (
          <>
            <TextField
              size="small"
              label="Ticket No."
              name="ticketNumber"
              value={formDetails.ticketNumber || ""}
              onChange={onInputChange}
              required
              disabled={rateContractMissing}
            />
            <TextField
              size="small"
              label="Ticket Creation Date"
              type="date"
              name="ticketCreationDate"
              value={
                formDetails.ticketCreationDate
                  ? new Date(formDetails.ticketCreationDate)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={onInputChange}
              InputLabelProps={{ shrink: true }}
              required
              disabled={rateContractMissing}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AddItemModal;
